import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axiosConfig';
import Pagination from '../components/Pagination';
import { useModal } from '../contexts/ModalContext';

const fmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const h = d.getHours();
    return (
        `${d.getMonth() + 1}/${d.getDate()} ` +
        `${h < 12 ? '오전' : '오후'} ${h % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')}`
    );
};

const place = (r) =>
    r.section?.name || `${r.facility?.name ?? '알 수 없는 시설'} 전체`;

/**
 * 지운 점검 기록.
 *
 * 기록을 지우는 것은 막지 않는다 -- 잘못 올린 기록도 있고, 그걸 못 지우게 하면
 * 틀린 것이 영영 통계에 남는다. 대신 사라지지 않고 여기 남고, 누가 언제 지웠
 * 는지가 함께 남는다. 억제력은 막는 데서가 아니라 남는 데서 나온다.
 *
 * 지금까지 되살리는 길은 API 에만 있었다. 잘못 지운 사람이 개발자에게 부탁해야
 * 했다는 뜻이다.
 */
const DeletedReportsPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const facilityId = searchParams.get('facilityId');
    const { showAlert, showConfirm } = useModal();

    const [items, setItems] = useState([]);
    const [pageInfo, setPageInfo] = useState({ page: 1, total: 0, limit: 20 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(
        async (page = 1, append = false) => {
            setLoading(true);
            try {
                const { data } = await api.get('/reports/deleted', {
                    params: { page, limit: 20, ...(facilityId ? { facilityId } : {}) },
                });
                setItems((prev) => (append ? [...prev, ...data.items] : data.items));
                setPageInfo({ page: data.page, total: data.total, limit: data.limit });
                setError(null);
            } catch (err) {
                console.error('Failed to load deleted reports', err);
                setError(
                    err.response?.status === 403
                        ? '지운 기록을 볼 권한이 없습니다.'
                        : '지운 기록을 불러오지 못했습니다.',
                );
            } finally {
                setLoading(false);
            }
        },
        [facilityId],
    );

    useEffect(() => {
        load(1);
    }, [load]);

    const restore = async (report) => {
        const ok = await showConfirm(
            '기록 되살리기',
            `${place(report)} · ${fmt(report.createdAt)} 기록을 되살릴까요? 원래 자리로 돌아갑니다.`,
            'primary',
            { confirm: '되살리기', cancel: '그대로 두기' },
        );
        if (!ok) return;
        try {
            await api.patch(`/reports/${report.id}/restore`);
            // 되살린 것은 이 목록에서 빠진다. 여기 남아 있으면 두 번 누르게 된다.
            setItems((prev) => prev.filter((r) => r.id !== report.id));
            setPageInfo((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
        } catch (err) {
            showAlert(
                '오류',
                '되살리지 못했습니다: ' + (err.response?.data?.message || err.message),
                'danger',
            );
        }
    };

    return (
        <div className="pb-5">
            <div className="d-flex align-items-center gap-2 mb-3">
                <button className="btn border-0 p-0" onClick={() => navigate(-1)}>
                    <i className="bi bi-chevron-left fs-5"></i>
                </button>
                <div>
                    <h4 className="fw-bold mb-1">지운 점검 기록</h4>
                    <p className="text-muted small mb-0">
                        지운 기록은 사라지지 않습니다. 되살리면 원래 자리로 돌아갑니다.
                    </p>
                </div>
            </div>

            {error ? (
                <div className="alert alert-warning">{error}</div>
            ) : loading && items.length === 0 ? (
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status"></div>
                </div>
            ) : items.length === 0 ? (
                <div className="bg-white rounded-4 border text-center p-5">
                    <i className="bi bi-trash3 display-6 d-block mb-3 text-muted opacity-50"></i>
                    <h6 className="fw-bold mb-1">지운 기록이 없습니다</h6>
                    <p className="text-muted small mb-0">
                        {facilityId ? '이 시설에서 지워진 기록이 없습니다.' : '아무도 기록을 지우지 않았습니다.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="text-muted small mb-2">전체 {pageInfo.total}건</div>
                    {items.map((r) => (
                        <div key={r.id} className="bg-white rounded-4 border p-3 mb-2">
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <span className="badge bg-light text-dark border fw-normal">
                                    {place(r)}
                                </span>
                                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>
                                    {r.reporter?.name || '알 수 없음'}
                                </span>
                                <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                                    {fmt(r.createdAt)} 점검
                                </span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                {/* 누가 지웠는지가 이 화면의 요점이다. 지운 사람을 물을 수
                                    없으면 남겨 두는 뜻이 없다. */}
                                <div className="text-muted flex-grow-1" style={{ fontSize: '0.75rem' }}>
                                    {r.deletedBy?.name ? `${r.deletedBy.name}이(가) ` : ''}
                                    {fmt(r.deletedAt)}에 지움
                                    {r.photoCount > 0 && ` · 사진 ${r.photoCount}장`}
                                </div>
                                <button
                                    className="btn btn-sm btn-white border rounded-pill px-3 text-nowrap"
                                    onClick={() => restore(r)}
                                >
                                    되살리기
                                </button>
                            </div>
                        </div>
                    ))}

                    <Pagination
                        page={pageInfo.page}
                        total={pageInfo.total}
                        limit={pageInfo.limit}
                        loading={loading}
                        onChange={load}
                    />
                </>
            )}
        </div>
    );
};

export default DeletedReportsPage;
